import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { walletService } from '../services/walletService';

const WalletContext = createContext(null);

export function WalletProvider({ children }) {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const [balance, setBalance] = useState(0);
  const [topUpHistory, setTopUpHistory] = useState([]);
  const [ticketHistory, setTicketHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const resetWallet = useCallback(() => {
    setBalance(0);
    setTopUpHistory([]);
    setTicketHistory([]);
    setIsLoading(false);
  }, []);

  const refreshWallet = useCallback(async () => {
    if (!isAuthenticated) {
      resetWallet();
      return;
    }

    setIsLoading(true);
    const [balanceRes, topUps, tickets] = await Promise.all([
      walletService.getBalance(),
      walletService.getTopUps(),
      walletService.getTickets(),
    ]);
    setBalance(balanceRes.balance);
    setTopUpHistory(topUps);
    setTicketHistory(tickets);
    setIsLoading(false);
  }, [isAuthenticated, resetWallet]);

  useEffect(() => {
    if (isAuthLoading) return;

    refreshWallet().catch(() => {
      resetWallet();
    });
  }, [isAuthLoading, refreshWallet, resetWallet]);

  useEffect(() => {
    const handleLogout = () => resetWallet();
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, [resetWallet]);

  const refreshAfterPurchase = useCallback((newBalance, tickets) => {
    setBalance(newBalance);
    setTicketHistory((prev) => [...tickets, ...prev]);
  }, []);

  const value = useMemo(
    () => ({
      balance,
      topUpHistory,
      ticketHistory,
      isLoading,
      setBalance,
      refreshWallet,
      refreshAfterPurchase,
    }),
    [
      balance,
      topUpHistory,
      ticketHistory,
      isLoading,
      refreshWallet,
      refreshAfterPurchase,
    ],
  );

  return (
    <WalletContext.Provider value={value}>{children}</WalletContext.Provider>
  );
}

export function useWallet() {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within WalletProvider');
  }
  return context;
}
